import React from 'react';
import { useParams } from 'react-router-dom';
import { blogPosts } from './blogPostsData';
import HeroSection from './HeroSection';
import JoinTheClub from '../HomePage/JoinTheClub';
import LazyImage from '../../components/LazyImage';
import BlogPostNotFound from './BlogPostNotFound';

const BlogPostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find(p => p.id === id);

  if (!post) {
    return <BlogPostNotFound />;
  }

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      
      {/* Main Article Content */}
      <article className="mx-auto px-4 md:px-10 lg:px-32 w-full pt-20">
        {/* Title Section */}
        <div className="mb-8">
          <div className="mb-6">
            <LazyImage 
              src="/images/Blog1Flag.webp"
              alt={post.title}
              className="w-full h-auto object-contain"
              sizes="100vw"
              priority={true}
              backgroundColor="transparent"
            />
          </div>
        </div>

        {/* Introduction */}
        {post.introduction && (
          <div className="mb-12 space-y-6">
            {post.introduction.map((paragraph, index) => (
              <p 
                key={index}
                className="text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] mb-4 last:mb-0 text-justify flex-1"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ))}
          </div>
        )}

      </article>

      {/* Red Flags Sections - Full Width */}
      {post.redFlags && post.redFlags.map((flag, index) => {
        const isOddFlag = flag.number === 1 || flag.number === 3 || flag.number === 5;
        const bgColor = isOddFlag ? 'bg-[#F6F6F6]' : 'bg-[#FFFFFF]';
        const isLastFlag = index === post.redFlags!.length - 1;
        const roundedClass = isOddFlag ? 'rounded-l-[22px]' : 'rounded-r-[22px]';
        
        return (
          <section key={flag.number} className={`w-full ${bgColor} py-8 md:py-12`}>
            <div className="mx-auto px-4 md:px-10 lg:px-32 w-full">
              <div className={`flex flex-col ${flag.imagePosition === 'right' ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-stretch`}>
                {/* Content */}
                <div className="w-full lg:w-1/2 flex flex-col">
                  <div className="mb-4">
                    <p className="font-[400] text-[#000] leading-[40px] text-[30px] md:text-[40px] lg:text-[32px]">
                      Red Flag #{flag.number}:
                    </p>
                    <h2 className="font-[700] text-[#000] mb-2 lg:leading-[40px] leading-[30px] text-[30px] md:text-[40px] lg:text-[32px]">
                      {flag.title}
                    </h2>
                  </div>
                  <p 
                    className="text-[16px] md:text-[20px] lg:text-[20px] text-[#000] leading-[29px] font-[400] mb-4 last:mb-0 text-justify flex-1"
                    dangerouslySetInnerHTML={{ __html: flag.content }}
                  />
                </div>

                {/* Image */}
                <div className="w-full lg:w-1/2 flex-shrink-0 flex flex-col">
                  <div className={`relative w-full h-full min-h-[300px] md:min-h-[400px] overflow-hidden ${roundedClass}`}>
                    <LazyImage 
                      src={flag.image}
                      alt={flag.title}
                      className="w-full h-full object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </div>
                </div>
              </div> 
              {/* Author Signature - only in last flag, under the image */}
                  {isLastFlag && post.authorSignature && (
                    <div className="lg:mt-14 mt-8">
                      <p className="flex flex-col gap-0">
                        <span className="font-[400] text-[#000] leading-normal text-[15px] md:text-[18px] lg:text-[20px]">Good Investing,</span>
                        <span className="font-[700] text-[#000] leading-normal text-[18px] md:text-[20px] lg:text-[25px] -mt-1">Rob Bruggeman, CFA</span>
                      </p>
                    </div>
                  )}
            </div>
          </section>
        );
      })}

      {/* Join The Club Section */}
      <JoinTheClub />
    </div>
  );
};

export default BlogPostDetail;
